import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Paper, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useAuth } from "@ui/components/AuthProvider";
import React, { useEffect, useState } from "react";

import { LoginContainer } from "./auth-page.style";

import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import MobileStepper from "@mui/material/MobileStepper";
import { signup } from "@ui/Auth/api";
import BackDropLoader from "components/app-loader/BackDropLoader.component";
import ClientLogo from "components/app-logo/app-logo.component";
import commonConfig from "config/commonConfig";
import enums from "helpers/enums";
import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { AiOutlineHome } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useAppApi } from "store/api-context";
import { useAppAuth } from "store/auth-context";
import { getAuthPath, getMainClient } from "utils/common/common.utils";
import { ROUTES } from "utils/constants/route.constants";
import { defaultSignUpFormValues } from "utils/forms-constants/forms-constants.util";
import {
  basicInfoFormSchema,
  companyInfoFormSchema,
} from "utils/forms-constants/schemas/signup-schema";
import { localStorageKeys } from "utils/local-utils/localstorage.utils";
import BasicInfoStep from "./steps/BasicInfoStep.component";
import CompanyInfoStep from "./steps/CompanyInfoStep.component";

export function SignUp() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const {
    setUserDetails,
    setIsAuthenticated,
    isMobileVerified,
    setIsMobileVerified,
    basicStepFormVisited,
    setBasicStepFormVisited,
  } = useAppAuth();
  const [validationSchema, setValidationSchema] = useState(basicInfoFormSchema);
  const [isLoading, setIsLoading] = useState(false);

  const mainClient = getMainClient(commonConfig.clients);

  const { state, fetchDivisions, setSelectedDivision, fetchOverview } =
    useAppApi();

  const onSubmit = async (data) => {
    const payload = {
      firstName: data?.firstname,
      lastName: data?.lastname,
      email: data?.email,
      phone: data?.mobile,
      password: data?.password,
      role: enums.roles.customer,
      companyDetails: {
        name: data?.company_name,
        address: {
          pincode: data?.pincode,
          street: data?.street,
          country: data?.country?.label,
          state: data?.state?.label,
          city: data?.city?.label,
        },
      },
    };

    setIsLoading(true);
    try {
      const res = await signup(
        {
          ...payload,
          client: mainClient?.id,
        },
        { isDivisionControlled: mainClient?.isDivisionControlled }
      );
      auth.signin(res?.data?.data?.user, res?.data?.data?.token);
      setIsMobileVerified(false);
      localStorage?.removeItem(localStorageKeys.isNumberVerified);
      setBasicStepFormVisited(false);
      setUserDetails(res?.data?.data?.user);
      setIsAuthenticated(res?.data?.data?.token);
      toast.success("Account has been created");
      navigate(ROUTES.HOME);
    } catch (err) {
      let errMsg = "";
      if (err?.response?.data?.message?.includes("duplicate")) {
        errMsg = "User already exists with this mobile number";
      } else {
        errMsg = err?.response?.data?.message;
      }
      toast.error(errMsg);
    } finally {
      setIsLoading(false);
      setIsMobileVerified(false);
    }
  };

  const methods = useForm({
    defaultValues: defaultSignUpFormValues,
    resolver: yupResolver(validationSchema),
    mode: "all",
  });

  const steps = [
    {
      label: "Personal Information",
      optional: "Provide some personal information about the employee",
      component: <BasicInfoStep basicStepFormVisited={basicStepFormVisited} />,
      schema: basicInfoFormSchema,
    },
    {
      label: "Company Location Information",
      optional: "Provide location information about the Company",
      component: <CompanyInfoStep />,
      schema: companyInfoFormSchema,
    },
  ];

  const maxSteps = steps.length;
  const totalSteps = () => {
    return steps.length;
  };

  const isLastSecondStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const { trigger, handleSubmit } = methods;

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  useEffect(() => {
    if (activeStep === 1) {
      setValidationSchema(companyInfoFormSchema);
    } else {
      setValidationSchema(basicInfoFormSchema);
    }
  }, [activeStep]);

  useEffect(() => {
    console.log("state", state);
    if (!isEmpty(state?.divisions)) {
      setSelectedDivision(state?.divisions?.[0]);
    }
  }, [state]);

  useEffect(() => {
    fetchDivisions();
    // setSelectedDivision(res?.divisions?.[0]);
  }, []);

  useEffect(() => {
    fetchOverview();
  }, []);

  return (
    <LoginContainer>
      <FormProvider {...methods}>
        <form noValidate autocomplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={2}
            sx={{
              textAlign: "center",
              maxWidth: {
                xs: 300,
                md: 600,
              },
              padding: {
                xs: "10px",
                md: "40px",
              },
            }}
          >
            <div>
              <ClientLogo src={state?.overviews[0]?.logo?.location} />
            </div>
            <Typography
              sx={{
                fontSize: "2rem",
                fontWeight: 600,
                textAlign: "center",
                marginBottom: "40px",
              }}
            >
              Sign up
            </Typography>
            <Grid container xs={12} spacing={2}>
              {steps[activeStep].component}
            </Grid>

            <MobileStepper
              sx={{
                width: "100%",
                marginTop: "3rem",
              }}
              variant="progress"
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              nextButton={
                isLastSecondStep() ? (
                  <Button type="submit" size="small">
                    Submit
                    <KeyboardArrowRight />
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={async (e) => {
                      e.preventDefault();
                      const isStepValid = await trigger();
                      if (!isMobileVerified) {
                        toast("You must validate your mobile number", {
                          style: {
                            background: "#FF6600",
                            padding: "4px",
                            color: "#fff",
                          },
                        });
                      }
                      if (isStepValid && isMobileVerified) {
                        setBasicStepFormVisited(true);
                        setActiveStep(1);
                      }
                    }}
                  >
                    Next
                    <KeyboardArrowRight />
                  </Button>
                )
              }
              backButton={
                <Button
                  size="small"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  <KeyboardArrowLeft />
                  Back
                </Button>
              }
            />
          </Paper>
        </form>
      </FormProvider>

      <BackDropLoader open={isLoading} />

      <Stack justifyContent="center" alignItems="center" gap={4}>
        <Stack marginTop="20px" direction="row" gap="20px">
          <Typography>Already singed up?</Typography>
          <Typography
            style={{
              cursor: "pointer",
              "&:hover": {
                borderBottom: "1px solid",
              },
            }}
            onClick={() => navigate(getAuthPath())}
          >
            Login
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{
            cursor: "pointer",
          }}
          onClick={() => navigate(ROUTES.HOME)}
        >
          <AiOutlineHome size={30} />
          <Typography>Back home</Typography>
        </Stack>
      </Stack>
    </LoginContainer>
  );
}
